import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Typography, useTheme } from '@mui/material';
import { sectionBackgrounds, resolveGradient } from '../../../theme';
import { getIconByName } from '../../../utils/iconMap';
const STEP_ICONS = ['LooksOne', 'LooksTwo', 'Looks3', 'Looks4', 'Looks5', 'Looks6'];
/**
 * ProcessSteps – step-by-step display. Supports label + gradient.
 */
const ProcessSteps = ({ label, gradient, steps = [], layout = 'vertical', defaultStepIcon, stepIconColor = 'white', stepIconBackground, stepTitleColor, stepTitleFontSize, stepDescriptionColor, stepDescriptionFontSize, connectorColor, accentColor, paddingTop, paddingBottom, paddingLeft, paddingRight, marginTop, marginBottom, maxWidth, borderRadius = '50%', boxShadow, fontFamily, fontWeight = 700, lineHeight = 1.6, componentAlignment = 'left', textAlign = 'left', }) => {
    const theme = useTheme();
    const background = gradient
        ? resolveGradient(gradient, sectionBackgrounds.hero)
        : 'transparent';
    const isHorizontal = layout === 'horizontal';
    const defaultIconBg = stepIconBackground ||
        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`;
    return (_jsx(Box, { component: "section", sx: {
            background,
            paddingTop: paddingTop || 3,
            paddingBottom: paddingBottom || 3,
            paddingLeft: paddingLeft || 0,
            paddingRight: paddingRight || 0,
            marginTop: marginTop || 0,
            marginBottom: marginBottom || 0,
            textAlign: componentAlignment,
        }, children: _jsxs(Box, { sx: { maxWidth: maxWidth || 900, mx: 'auto', px: 2 }, children: [label && (_jsx(Typography, { variant: "overline", sx: { display: 'block', mb: 2, color: 'text.secondary', letterSpacing: 1 }, children: label })), _jsx(Box, { sx: {
                        display: 'flex',
                        flexDirection: isHorizontal ? 'row' : 'column',
                        gap: 3,
                        flexWrap: isHorizontal ? 'wrap' : 'nowrap',
                    }, children: steps.map((step, index) => {
                        const iconName = defaultStepIcon || step.icon || STEP_ICONS[index] || 'LooksOne';
                        const IconComponent = getIconByName(iconName);
                        const isLastStep = index === steps.length - 1;
                        return (_jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: isHorizontal ? 'center' : 'flex-start',
                                flex: isHorizontal ? '1 1 250px' : 'auto',
                                minWidth: isHorizontal ? 250 : 'auto',
                                position: 'relative',
                            }, children: [_jsxs(Box, { sx: {
                                        display: 'flex',
                                        flexDirection: isHorizontal ? 'column' : 'row',
                                        alignItems: isHorizontal ? 'center' : 'flex-start',
                                        gap: 2,
                                        flex: 1,
                                        width: '100%',
                                    }, children: [_jsx(Box, { sx: {
                                                width: 80,
                                                height: 80,
                                                borderRadius: borderRadius === '50%' ? '50%' : borderRadius,
                                                background: defaultIconBg,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: stepIconColor || 'white',
                                                boxShadow: boxShadow || '0 4px 12px rgba(0,0,0,0.15)',
                                                flexShrink: 0,
                                                position: 'relative',
                                                zIndex: 2,
                                            }, children: _jsx(IconComponent, { sx: { fontSize: 40 } }) }), _jsxs(Box, { sx: { flex: 1, textAlign: isHorizontal ? 'center' : textAlign, pt: isHorizontal ? 0 : 1 }, children: [_jsx(Typography, { sx: {
                                                        fontSize: stepTitleFontSize || '1.25rem',
                                                        fontWeight: Number(fontWeight) || 700,
                                                        fontFamily: fontFamily || undefined,
                                                        mb: 1,
                                                        color: stepTitleColor || 'text.primary',
                                                    }, children: step.title }), _jsx(Typography, { variant: "body2", sx: {
                                                        fontSize: stepDescriptionFontSize || '0.95rem',
                                                        fontFamily: fontFamily || undefined,
                                                        color: stepDescriptionColor || 'text.secondary',
                                                        lineHeight: lineHeight ?? 1.6,
                                                    }, children: step.description })] })] }), !isLastStep && (_jsx(Box, { sx: {
                                        position: isHorizontal ? 'absolute' : 'relative',
                                        ...(isHorizontal
                                            ? {
                                                top: 40,
                                                left: '50%',
                                                width: 'calc(100% + 24px)',
                                                height: 2,
                                                transform: 'translateX(40px)',
                                            }
                                            : {
                                                width: 2,
                                                height: 40,
                                                ml: '39px',
                                                my: -1,
                                            }),
                                        backgroundColor: connectorColor || accentColor || 'primary.main',
                                        zIndex: 1,
                                    } }))] }, index));
                    }) })] }) }));
};
export default ProcessSteps;
